/* eslint-disable complexity */
import React, { useMemo } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';

import { getProductConsolidationUrl, PRODUCT_ROUTES, ROUTES, useWorkspaceSelector } from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';

import { IPredictManagementContext, PredictManagementContext } from '../../context/predict-management-context';
import { addNormalisedErrorMessage } from '../../../flash-list/flash-list-actions';
import { getFYConfigFromDayMonthEnd } from '../../../utils/financial-year-end';
import { PredictTrialExpired } from '../predict-trial-expired';
import { useConsolidationDataContext } from '../../context/consolidation-data-context';
import { useConsolidationManagementRoutes } from '../../hooks/useConsolidationManagementRoutes';

export const PredictConsolidationManagement = () => {
  const { routes, redirect } = useConsolidationManagementRoutes();

  const memoizedAddNormalisedErrorMessage = useMemo(() => addNormalisedErrorMessage(), []);

  const { consolidation } = useConsolidationDataContext();

  const workspace = useWorkspaceSelector(consolidation?.workspace_id || '');
  const baseUrl = getProductConsolidationUrl(
    consolidation?.workspace_id || '',
    consolidation?.id || '',
    PRODUCT_ROUTES.PREDICT
  );
  const isPro = workspace?.subscription_state === 'trial' || workspace?.subscription_state === 'active';

  const predictManagementContent: IPredictManagementContext = useMemo(() => {
    return {
      financialConfig: consolidation
        ? getFYConfigFromDayMonthEnd(consolidation.financial_year_end_day, consolidation.financial_year_end_month)
        : undefined,
      organisation: null,
      currency: consolidation?.display_currency || null,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      refetchOrganisation: () => {},
      addErrorMessage: memoizedAddNormalisedErrorMessage
    };
  }, [memoizedAddNormalisedErrorMessage, consolidation]);

  if (!workspace || !consolidation) {
    return <UILoader isFull={true} />;
  }

  if (!isPro) {
    return (
      <Routes>
        <Route path={ROUTES.TRIAL_EXPIRED} element={<PredictTrialExpired />} />
        <Route path="*" element={<Navigate to={`${baseUrl}${ROUTES.TRIAL_EXPIRED}`} />} />
      </Routes>
    );
  }

  return (
    <PredictManagementContext.Provider value={predictManagementContent}>
      <Routes>
        {routes.map(({ name, route, component: Component }) => (
          <Route key={name} path={route} element={<Component />} />
        ))}
        {redirect && <Route path="*" element={<Navigate replace to={redirect.to} />} />}
      </Routes>
    </PredictManagementContext.Provider>
  );
};
